import React, { useContext, useEffect, useState } from 'react';

import { LinearProgress, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { EditableList } from './components/EditableList';
import { WhiteboardContext } from './context/WhiteboardContext';
import WhiteboardPdfContainer from './context/WhiteboardPdfContainer';
import { WhiteboardSessionContext } from './context/WhiteboardSessionContext';
import { useWhiteboardPages } from './hooks';
import { PageActions } from './state/WhiteboardSessionState';
import { type Page } from './types';
import { Status } from '../../types/apiTypes';
import { UserRoles } from '../roster/types';

export interface WhiteboardSidebarContentProps {}

const LinearProgressSpacer = styled('div')`
    height: 4px;
`;

const WhiteboardSidebarContentHolder = styled(Box)`
    height: auto;
`;

const WhiteboardSidebarContent: React.FC<WhiteboardSidebarContentProps> = () => {
    const { updatePages, deletePages, createPages, userInfo } = useContext(WhiteboardContext);
    const { persist, changePage, sessionOptions, whiteboardSessionState, dispatchWhiteboardSessionAction } =
        useContext(WhiteboardSessionContext);
    const { importPdf, deletePdf, fileStatus, progress } = useContainer(WhiteboardPdfContainer);
    const { currentPage, session } = whiteboardSessionState;
    const { t } = useTranslation(['whiteboard', 'common']);

    const pages = useWhiteboardPages();
    const [pagesLoading, setPagesLoading] = useState(true);
    const [requestedPage, setRequestedPage] = useState<Page | undefined>();

    useEffect(() => {
        if (requestedPage) {
            persist();
            changePage(requestedPage);
            setRequestedPage(undefined);
        }
    }, [requestedPage, changePage, persist]);

    /**
     * Loading is considered complete when the
     * user has a page to view.
     */
    useEffect(() => {
        if (currentPage) {
            setPagesLoading(false);
        }
    }, [currentPage]);

    const listLoading = () => {
        if (pagesLoading || fileStatus === Status.InProgress) {
            return <LinearProgress aria-busy="true" aria-live="polite" />;
        }

        return <LinearProgressSpacer />;
    };

    const handlePageUpdate = (page: Page) => {
        updatePages([page]);
    };

    const handleDeletePage = (pageToDelete: Page) => {
        deletePages([pageToDelete.page_id]);
        dispatchWhiteboardSessionAction({
            type: PageActions.Deleted,
            payload: { page_ids: [pageToDelete.page_id], local: true }
        });
    };

    const handleDeletePdf = async (fileName) => {
        if (!session) return;

        deletePdf(session, fileName);
    };

    const handleImportPdf = async (errorCb?: (error: any) => void) => {
        if (!session) {
            return;
        }

        try {
            await importPdf(session!);
        } catch (e: any) {
            errorCb?.(e);
        }
    };

    const handleNewPage = async () => {
        if (!session || !userInfo) {
            return;
        }

        setPagesLoading(true);

        const lastPageOrder = Math.max(...whiteboardSessionState.pages.map((page) => page.order));

        const [newPage] = await createPages([
            {
                session_id: session.session_id,
                name: '', // Putting in an empty string will make the editable list use a default translatable name instead.
                order: lastPageOrder + 1,
                is_locked: false,
                write_access: UserRoles.Student
            }
        ]);

        if (newPage) {
            dispatchWhiteboardSessionAction({ type: PageActions.Created, payload: { pages: [newPage] } });
        }

        setPagesLoading(false);
    };

    if (!sessionOptions.showPageList || !currentPage) {
        return <></>;
    }

    return (
        <WhiteboardSidebarContentHolder>
            {listLoading()}
            <EditableList<Page>
                defaultName={t('whiteboard:defaultPageName')}
                items={pages.primaryPages}
                templatePages={pages.templatePages}
                secondaryItems={pages.secondaryPages}
                pdfFiles={pages.pdfFiles}
                secondaryItemsHeading={t('whiteboard:otherPages')}
                currentItem={currentPage}
                disabled={sessionOptions.isPresentationViewer}
                onItemRename={sessionOptions.canEditPages ? handlePageUpdate : undefined}
                onItemSelect={setRequestedPage}
                onItemCreate={
                    sessionOptions.canEditPages && !sessionOptions.isPresentationViewer ? handleNewPage : undefined
                }
                onItemLock={sessionOptions.canLockPages ? handlePageUpdate : undefined}
                // onItemCopy={canEditPages ? handleCopyPage : undefined}
                onItemDelete={sessionOptions.canEditPages ? handleDeletePage : undefined}
                onImportPdf={
                    sessionOptions.canEditPages && !sessionOptions.isPresentationViewer ? handleImportPdf : undefined
                }
                onPdfDelete={sessionOptions.canEditPages ? handleDeletePdf : undefined}
                fileStatus={fileStatus}
                fileProgress={progress}
            />
        </WhiteboardSidebarContentHolder>
    );
};

export default WhiteboardSidebarContent;
